import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Loader2, Trash2, Pencil, Ban, CheckCircle2, ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { UserDetailDrawer } from "@/pages/UserDetailDrawer";

const ROLE_OPTIONS = [
  { key: "admin_ops", label: "Admin Ops" },
  { key: "kewag_admin", label: "KEWAG Admin" },
  { key: "moderator", label: "Moderator" },
  { key: "support", label: "Support" },
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.key, r.label]));

function RoleBadge({ u }) {
  if (u.is_owner) return <span data-testid={`user-role-badge-${u.id}`} className="rounded-md border border-fundio-gold bg-fundio-gold/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-fundio-gold">Super Admin / Owner</span>;
  if (u.role === "admin_ops") return <span data-testid={`user-role-badge-${u.id}`} className="rounded-md border border-status-info-border bg-status-info-bg px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-status-info">Admin Ops</span>;
  return <span data-testid={`user-role-badge-${u.id}`} className="rounded-md border border-status-warning-border bg-status-warning-bg px-2 py-0.5 text-xs font-medium text-status-warning">{u.role_label || ROLE_LABEL[u.role] || u.role}</span>;
}

export default function Users() {
  const { user, can } = useAuth();
  const writable = can("users", "write");
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "moderator", password: "" });
  const [saving, setSaving] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [del, setDel] = useState(null); // user pending delete

  const load = useCallback(() => {
    api.get("/users").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Benutzer angelegt");
      setOpen(false); setForm({ name: "", email: "", role: "moderator", password: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openDrawer = (id) => { setDrawerId(id); setDrawerOpen(true); };

  const toggleStatus = async (u) => {
    try { await api.patch(`/users/${u.id}`, { status: u.status === "active" ? "inactive" : "active" }); toast.success("Status aktualisiert"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    const u = del; setDel(null);
    try { await api.delete(`/users/${u.id}`); toast.success("Benutzer gelöscht"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Benutzer & Rollen" subtitle="Konten, Rollen und Sicherheit für das FUNDIO Control OS.">
        {writable && <Button data-testid="user-create-button" onClick={() => setOpen(true)} className="border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight"><Plus className="h-4 w-4" /> Neuer Benutzer</Button>}
      </PageHeader>

      <Panel testid="users-panel" noPad>
        {items === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : items.length === 0 ? (
          <div className="p-4"><EmptyState icon={UsersIcon} title="Keine Benutzer" testid="users-empty" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                  <th className="px-5 py-3">Name</th><th className="px-5 py-3">E-Mail</th><th className="px-5 py-3">Rolle</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">2FA</th><th className="px-5 py-3">Passwortwechsel</th><th className="px-5 py-3">Letzte Anmeldung</th><th className="px-5 py-3 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fundio-border">
                {items.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="bg-white hover:bg-fundio-subtle/40">
                    <td className="px-5 py-3.5 font-medium text-fundio-ink">
                      {u.name}
                      {u.is_demo && <span data-testid={`user-demo-badge-${u.id}`} className="ml-2 inline-flex items-center rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-status-warning">DEMO / INAKTIV</span>}
                    </td>
                    <td className="px-5 py-3.5 text-fundio-muted">{u.email}</td>
                    <td className="px-5 py-3.5"><RoleBadge u={u} /></td>
                    <td className="px-5 py-3.5"><span className={`text-xs font-medium ${u.status === "active" ? "text-status-success" : "text-fundio-faint"}`}>{u.status === "active" ? "Aktiv" : "Inaktiv"}</span></td>
                    <td className="px-5 py-3.5">{u.two_factor_enabled ? <span className="inline-flex items-center gap-1 text-xs font-medium text-status-success"><ShieldCheck className="h-3.5 w-3.5" /> Aktiv</span> : <span className="text-xs text-fundio-faint">—</span>}</td>
                    <td className="px-5 py-3.5">{u.must_change_password ? <span className="text-xs font-medium text-status-warning">Erforderlich</span> : <span className="text-xs text-fundio-faint">—</span>}</td>
                    <td className="px-5 py-3.5 text-fundio-faint">{u.last_login ? formatDate(u.last_login) : "—"}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`user-open-${u.id}`} onClick={() => openDrawer(u.id)} title="Öffnen / Bearbeiten" className="inline-flex items-center gap-1 rounded-md border border-fundio-border px-2 py-1 text-xs font-medium text-fundio-ink transition-colors hover:border-fundio-gold hover:text-fundio-gold"><Pencil className="h-3.5 w-3.5" /> Öffnen</button>
                        {isSuper && !u.is_protected_owner && (
                          <button data-testid={`user-toggle-status-${u.id}`} onClick={() => toggleStatus(u)} title={u.status === "active" ? "Deaktivieren" : "Aktivieren"} className="text-fundio-faint transition-colors hover:text-status-warning">{u.status === "active" ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}</button>
                        )}
                        {isSuper && !u.is_owner && u.id !== user.id && (
                          <button data-testid={`user-delete-${u.id}`} onClick={() => setDel(u)} title="Löschen" className="text-fundio-faint transition-colors hover:text-status-danger"><Trash2 className="h-4 w-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <UserDetailDrawer userId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={load} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Neuer Benutzer</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Name</Label><Input data-testid="user-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">E-Mail</Label><Input data-testid="user-field-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Rolle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select-dropdown" className="input-dark"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Passwort</Label><Input data-testid="user-field-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-fundio-muted">Abbrechen</Button>
            <Button data-testid="user-save-button" disabled={saving || !form.name || !form.email || form.password.length < 6} onClick={save} className="border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="user-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
            <AlertDialogDescription>{del?.email} wird endgültig gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm-ok" onClick={doDelete} className="bg-status-danger text-white hover:bg-status-danger/90">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
